import React, { useState, useEffect } from "react";
import {
  X,
  Phone,
  Users,
  Filter,
  Play,
  AlertCircle,
  CheckCircle,
  Info,
  ChevronDown,
  UserCheck,
  Layers,
} from "lucide-react";
import FilterPresetSelector from "./FilterPresetSelector";
import AdvancedFilterBuilder from "./AdvancedFilterBuilder";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

/**
 * AutoAssignCallbacksModal
 *
 * 4-step wizard to auto-assign callback leads (leadStatus = "nurture").
 * Mirrors the existing AutoAssignLeadsModal but:
 *   1. Automatically locks the pool to leadStatus = "nurture"
 *   2. Adds an "Assigned To" filter so you can pull specific agents'
 *      callbacks and redistribute them
 *
 * Props:
 *   isOpen     - boolean
 *   onClose    - () => void
 *   onSuccess  - () => void
 *   teamId     - string | undefined
 */

const STEPS = [
  { num: 1, label: "Filter Callbacks" },
  { num: 2, label: "Assignment Settings" },
  { num: 3, label: "Select Agents" },
  { num: 4, label: "Review & Execute" },
];

const withNurtureCondition = (config) => {
  const nurtureCondition = {
    field: "leadStatus",
    operator: "equals",
    value: "nurture",
  };
  if (!config) return { logic: "AND", conditions: [nurtureCondition] };
  const already = config.conditions?.some(
    (c) => c.field === "leadStatus" && c.value === "nurture",
  );
  if (already) return config;
  return {
    ...config,
    conditions: [...(config.conditions || []), nurtureCondition],
  };
};

const AutoAssignCallbacksModal = ({ isOpen, onClose, onSuccess, teamId }) => {
  const { isDarkMode } = useTheme();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Step 1 — Lead filter
  const [filterMode, setFilterMode] = useState("preset");
  const [filterPresets, setFilterPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [filterConfig, setFilterConfig] = useState({
    logic: "AND",
    conditions: [],
  });

  // Step 1 — Assigned-to filter
  const [filterByAssignedTo, setFilterByAssignedTo] = useState(false);
  const [assignedToUserIds, setAssignedToUserIds] = useState([]);

  // Step 2 — Settings
  const [leadsPerUser, setLeadsPerUser] = useState(10);
  const [minCycleCount, setMinCycleCount] = useState(2);
  const [assignmentStrategy, setAssignmentStrategy] = useState("round_robin");

  // Step 3 — User pool
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");

  // Step 4 — Preview
  const [previewData, setPreviewData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (isOpen) {
      resetState();
      loadInitialData();
    }
  }, [isOpen, teamId]);

  const resetState = () => {
    setStep(1);
    setError("");
    setSuccess("");
    setFilterMode("preset");
    setSelectedPresetId(null);
    setFilterConfig({ logic: "AND", conditions: [] });
    setFilterByAssignedTo(false);
    setAssignedToUserIds([]);
    setSelectedUsers([]);
    setUserSearchQuery("");
    setPreviewData(null);
    setLeadsPerUser(10);
    setMinCycleCount(2);
    setAssignmentStrategy("round_robin");
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError("");
      const [presetsRes, userRes] = await Promise.all([
        apiService.getFilterPresets("lead"),
        apiService.getMe(),
      ]);
      const usablePresets = (presetsRes.data || []).filter(
        (p) => !p.name?.startsWith("__callback_daily__"),
      );
      setFilterPresets(usablePresets);
      setCurrentUser(userRes.user);
      await loadUsers();
    } catch (err) {
      setError("Failed to load data: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    if (teamId) {
      const res = await apiService.getTeamMembers(teamId);
      const members = res.members || res.data || [];
      setAllUsers(
        members
          .filter((m) => m.status === "active" && m.user)
          .map((m) => ({
            id: m.user.id,
            name: m.user.name,
            email: m.user.email,
            profileImageUrl: m.user.profileImageUrl,
            role: m.role,
          })),
      );
    } else {
      const res = await apiService.getCompanyUsers();
      setAllUsers(res.data || res.users || []);
    }
  };

  const handleSelectPreset = async (presetId) => {
    if (!presetId) {
      setSelectedPresetId(null);
      setFilterConfig({ logic: "AND", conditions: [] });
      return;
    }
    try {
      const res = await apiService.getFilterPreset(presetId);
      setSelectedPresetId(presetId);
      setFilterConfig(
        res.data.filterConfig || { logic: "AND", conditions: [] },
      );
    } catch {
      setError("Failed to load filter preset");
    }
  };

  const handleDeletePreset = async (presetId) => {
    try {
      await apiService.deleteFilterPreset(presetId);
      setFilterPresets((p) => p.filter((x) => x.id !== presetId));
      if (selectedPresetId === presetId) {
        setSelectedPresetId(null);
        setFilterConfig({ logic: "AND", conditions: [] });
      }
    } catch {
      setError("Failed to delete preset");
    }
  };

  const handleSetDefaultPreset = async (presetId) => {
    try {
      await apiService.updateFilterPreset(presetId, { isDefault: true });
      await loadInitialData();
    } catch {
      setError("Failed to set default preset");
    }
  };

  const buildEffectiveFilters = () => {
    let base =
      filterMode === "none"
        ? null
        : filterConfig.conditions?.length > 0
          ? filterConfig
          : null;

    if (filterByAssignedTo && assignedToUserIds.length > 0) {
      const assignedCondition = {
        field: "assignedTo",
        operator: "in",
        value: assignedToUserIds,
      };
      base = {
        logic: base?.logic || "AND",
        conditions: [...(base?.conditions || []), assignedCondition],
      };
    }
    return withNurtureCondition(base);
  };

  const filteredUsers = allUsers.filter((u) => {
    if (!userSearchQuery) return true;
    const q = userSearchQuery.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  });

  const toggleUser = (id) =>
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAllUsers = () =>
    setSelectedUsers(
      selectedUsers.length === filteredUsers.length
        ? []
        : filteredUsers.map((u) => u.id),
    );

  const toggleAssignedToUser = (id) =>
    setAssignedToUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const canProceed = () => {
    switch (step) {
      case 1:
        if (filterByAssignedTo && assignedToUserIds.length === 0) return false;
        return true;
      case 2:
        return parseInt(leadsPerUser) > 0 && parseInt(minCycleCount) >= 1;
      case 3:
        return selectedUsers.length >= 1;
      default:
        return true;
    }
  };

  const getPreviewData = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiService.previewAutoAssignment({
        filterPresetId: null,
        advancedFilters: buildEffectiveFilters(),
        leadsPerUser: parseInt(leadsPerUser),
        userIds: selectedUsers,
        minCycleCount: parseInt(minCycleCount),
        assignmentStrategy,
        teamId,
      });
      setPreviewData(response.data);
      setStep(4);
    } catch (err) {
      setError(err.message || "Failed to generate preview");
    } finally {
      setLoading(false);
    }
  };

  const executeAssignment = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const response = await apiService.executeAutoAssignment({
        filterPresetId: null,
        advancedFilters: buildEffectiveFilters(),
        leadsPerUser: parseInt(leadsPerUser),
        userIds: selectedUsers,
        minCycleCount: parseInt(minCycleCount),
        assignmentStrategy,
        teamId,
        targetStatus: "nurture",
      });
      setSuccess(
        `Successfully assigned ${response.data.totalAssigned} callbacks to ${response.data.usersAssigned} agents.`,
      );
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to execute assignment");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Derived theme helpers
  const cardCls = `${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`;
  const labelCls = `block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;
  const inputCls = `w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:border-amber-500 transition-colors ${
    isDarkMode
      ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;
  const mutedText = isDarkMode ? "text-gray-400" : "text-gray-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div
        className={`w-full max-w-4xl rounded-xl shadow-2xl flex flex-col ${
          isDarkMode ? "bg-gray-900" : "bg-white"
        }`}
        style={{ height: "calc(100vh - 4rem)", maxHeight: "900px" }}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b flex-shrink-0 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Phone size={20} className="text-amber-400" />
            </div>
            <div>
              <h2
                className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Auto-Assign Callbacks
              </h2>
              <p className={`text-sm mt-0.5 ${mutedText}`}>
                Step {step} of 4:{" "}
                <span
                  className={isDarkMode ? "text-gray-300" : "text-gray-600"}
                >
                  {STEPS.find((s) => s.num === step)?.label}
                </span>
                {teamId && (
                  <span className="ml-2 text-xs text-amber-400">
                    · Team members only
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white hover:bg-gray-800"
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            <X size={22} />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 pt-4 pb-3 flex-shrink-0">
          <div className="flex gap-1.5">
            {STEPS.map((s) => (
              <div key={s.num} className="flex-1">
                <div
                  className={`h-1.5 rounded-full transition-colors ${
                    s.num <= step
                      ? "bg-amber-500"
                      : isDarkMode
                        ? "bg-gray-700"
                        : "bg-gray-200"
                  }`}
                />
                <div
                  className={`text-xs mt-1 text-center transition-colors ${
                    s.num === step
                      ? "text-amber-400"
                      : s.num < step
                        ? mutedText
                        : isDarkMode
                          ? "text-gray-600"
                          : "text-gray-400"
                  }`}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
          {error && (
            <div className="mb-4 mt-2 flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/40 rounded-lg">
              <AlertCircle
                size={15}
                className="text-red-400 mt-0.5 flex-shrink-0"
              />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 mt-2 flex items-start gap-2 p-3 bg-green-500/10 border border-green-500/40 rounded-lg">
              <CheckCircle
                size={15}
                className="text-green-400 mt-0.5 flex-shrink-0"
              />
              <p className="text-sm text-green-400">{success}</p>
            </div>
          )}

          {/* ── STEP 1: Filter ── */}
          {step === 1 && (
            <div className="space-y-5 mt-3">
              <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <Info
                  size={14}
                  className="text-amber-400 mt-0.5 flex-shrink-0"
                />
                <p className="text-xs text-amber-300/80">
                  Only <strong>Nurture (Callback)</strong> leads are targeted —
                  this is enforced automatically. Use the options below to
                  further narrow the pool, e.g. by postcode or which agent
                  currently holds them.
                </p>
              </div>

              {/* Filter mode toggle */}
              <div
                className={`flex rounded-lg border overflow-hidden text-sm ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {[
                  { id: "preset", label: "Use Saved Preset" },
                  { id: "builder", label: "Build Filter" },
                  { id: "none", label: "All Callbacks" },
                ].map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => {
                      setFilterMode(mode.id);
                      if (mode.id === "none") {
                        setSelectedPresetId(null);
                        setFilterConfig({ logic: "AND", conditions: [] });
                      }
                    }}
                    className={`flex-1 py-2 px-4 transition-colors ${
                      filterMode === mode.id
                        ? isDarkMode
                          ? "bg-gray-700 text-white"
                          : "bg-gray-100 text-gray-900"
                        : isDarkMode
                          ? "text-gray-400 hover:text-white hover:bg-gray-800"
                          : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>

              {filterMode === "preset" && (
                <div className="space-y-2">
                  <label className={labelCls}>Select Filter Preset</label>
                  <FilterPresetSelector
                    presets={filterPresets}
                    selectedPresetId={selectedPresetId}
                    onSelectPreset={handleSelectPreset}
                    onDeletePreset={handleDeletePreset}
                    onSetDefault={handleSetDefaultPreset}
                    currentUserId={currentUser?.id}
                  />
                  {selectedPresetId && filterConfig.conditions?.length > 0 && (
                    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-300/80 flex items-center gap-2">
                      <Filter size={13} />
                      {filterConfig.conditions.length} condition(s) loaded from
                      preset
                    </div>
                  )}
                </div>
              )}

              {filterMode === "builder" && (
                <AdvancedFilterBuilder
                  onApply={(config) => {
                    setFilterConfig(config);
                    setSelectedPresetId(null);
                  }}
                  initialFilters={filterConfig}
                  showSaveButton={false}
                />
              )}

              {filterMode === "none" && (
                <div
                  className={`p-4 border rounded-lg text-sm flex items-center gap-2 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-gray-400"
                      : "bg-gray-50 border-gray-200 text-gray-500"
                  }`}
                >
                  <Layers size={16} className={`flex-shrink-0 ${mutedText}`} />
                  All callback leads (nurture status) in the system will be
                  included in the assignment pool.
                </div>
              )}

              {/* ── Assigned-to filter ── */}
              <div
                className={`border rounded-lg overflow-hidden ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <button
                  onClick={() => {
                    setFilterByAssignedTo((v) => !v);
                    if (filterByAssignedTo) setAssignedToUserIds([]);
                  }}
                  className={`w-full flex items-center justify-between p-4 text-sm transition-colors ${
                    isDarkMode
                      ? "text-gray-300 hover:bg-gray-800/60"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="flex items-center gap-2 font-medium">
                    <UserCheck size={16} className="text-amber-400" />
                    Filter by Currently Assigned Agent
                    <span className={`text-xs font-normal ${mutedText}`}>
                      optional
                    </span>
                  </span>
                  <div className="flex items-center gap-2">
                    {filterByAssignedTo && assignedToUserIds.length > 0 && (
                      <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">
                        {assignedToUserIds.length} selected
                      </span>
                    )}
                    <ChevronDown
                      size={15}
                      className={`${mutedText} transition-transform ${
                        filterByAssignedTo ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </button>

                {filterByAssignedTo && (
                  <div
                    className={`border-t p-4 space-y-3 ${
                      isDarkMode ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <p className={`text-xs ${mutedText}`}>
                      Only pull callbacks currently held by these agents. Useful
                      for taking a specific rep's callbacks and redistributing
                      them across the team.
                    </p>
                    {allUsers.length === 0 ? (
                      <p className={`text-xs ${mutedText}`}>
                        No agents loaded yet.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                        {allUsers.map((u) => (
                          <label
                            key={u.id}
                            className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer border transition-colors text-sm ${
                              assignedToUserIds.includes(u.id)
                                ? "bg-amber-500/15 border-amber-500/40 text-white"
                                : isDarkMode
                                  ? "bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600"
                                  : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={assignedToUserIds.includes(u.id)}
                              onChange={() => toggleAssignedToUser(u.id)}
                              className={`w-4 h-4 rounded text-amber-500 focus:ring-amber-500 flex-shrink-0 ${
                                isDarkMode
                                  ? "bg-gray-700 border-gray-600"
                                  : "bg-white border-gray-300"
                              }`}
                            />
                            <div className="flex items-center gap-2 min-w-0">
                              <div
                                className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-medium overflow-hidden ${
                                  isDarkMode ? "bg-gray-600" : "bg-gray-200"
                                }`}
                              >
                                {u.profileImageUrl ? (
                                  <img
                                    src={u.profileImageUrl}
                                    alt={u.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  u.name?.charAt(0).toUpperCase()
                                )}
                              </div>
                              <span className="truncate text-xs">{u.name}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── STEP 2: Settings ── */}
          {step === 2 && (
            <div className="space-y-6 mt-4">
              <div>
                <label className={labelCls}>Callbacks Per Agent</label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={leadsPerUser}
                  onChange={(e) => setLeadsPerUser(e.target.value)}
                  className={inputCls}
                />
                <p className={`text-xs mt-1 ${mutedText}`}>
                  Maximum callbacks to assign to each agent
                </p>
              </div>

              <div>
                <label className={labelCls}>Minimum Cycle Count</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={minCycleCount}
                  onChange={(e) => setMinCycleCount(e.target.value)}
                  className={inputCls}
                />
                <p className={`text-xs mt-1 ${mutedText}`}>
                  A callback must be seen by at least this many different agents
                  before it can cycle back to the same person
                </p>
              </div>

              <div>
                <label className={labelCls}>Assignment Strategy</label>
                <select
                  value={assignmentStrategy}
                  onChange={(e) => setAssignmentStrategy(e.target.value)}
                  className={inputCls}
                >
                  <option value="round_robin">
                    Round Robin — Equal distribution
                  </option>
                  <option value="workload_balanced">
                    Workload Balanced — Agents with fewer active leads first
                  </option>
                  <option value="performance_based">
                    Performance Based — Higher converters first
                  </option>
                </select>
              </div>
            </div>
          )}

          {/* ── STEP 3: Agent selection ── */}
          {step === 3 && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className={`text-base font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Select Agents for Assignment Pool
                  </h3>
                  <p className={`text-sm mt-0.5 ${mutedText}`}>
                    {selectedUsers.length} of {filteredUsers.length} selected
                  </p>
                </div>
                <button
                  onClick={toggleAllUsers}
                  disabled={filteredUsers.length === 0}
                  className={`px-3 py-1.5 text-xs rounded-lg transition-colors disabled:opacity-50 ${
                    isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600 text-white"
                      : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                  }`}
                >
                  {selectedUsers.length === filteredUsers.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>

              <input
                type="text"
                placeholder="Search agents..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className={inputCls}
              />

              <div
                className={`border rounded-lg p-2 overflow-y-auto ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
                style={{ maxHeight: "400px" }}
              >
                {loading ? (
                  <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className={`text-center py-10 text-sm ${mutedText}`}>
                    <Users size={36} className="mx-auto mb-2 opacity-30" />
                    No agents found
                    {teamId && (
                      <p className="text-xs mt-1">
                        Make sure team members are active
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {filteredUsers.map((user) => (
                      <label
                        key={user.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border ${
                          selectedUsers.includes(user.id)
                            ? "bg-amber-500/15 border-amber-500/40"
                            : isDarkMode
                              ? "bg-gray-800 border-gray-700 hover:border-gray-600"
                              : "bg-white border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUser(user.id)}
                          className={`w-4 h-4 rounded text-amber-500 focus:ring-amber-500 flex-shrink-0 ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600"
                              : "bg-white border-gray-300"
                          }`}
                        />
                        <div
                          className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-medium overflow-hidden ${
                            isDarkMode
                              ? "bg-gray-700 text-gray-300"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {user.profileImageUrl ? (
                            <img
                              src={user.profileImageUrl}
                              alt={user.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            user.name?.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-sm font-medium truncate ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {user.name}
                            </span>
                            {user.role && (
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                  isDarkMode
                                    ? "bg-gray-700 text-gray-400"
                                    : "bg-gray-100 text-gray-500"
                                }`}
                              >
                                {user.role}
                              </span>
                            )}
                          </div>
                          <div className={`text-xs truncate ${mutedText}`}>
                            {user.email}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── STEP 4: Preview ── */}
          {step === 4 && previewData && (
            <div className="space-y-5 mt-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  {
                    label: "Callbacks Available",
                    value: previewData.totalLeadsAvailable,
                    color: isDarkMode ? "text-white" : "text-gray-900",
                  },
                  {
                    label: "Will Be Assigned",
                    value: previewData.totalToAssign,
                    color: "text-amber-400",
                  },
                  {
                    label: "Agents",
                    value: previewData.assignments?.length ?? 0,
                    color: "text-blue-400",
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    className={`p-4 rounded-lg border ${cardCls}`}
                  >
                    <div className={`text-xs mb-1 ${mutedText}`}>{s.label}</div>
                    <div className={`text-2xl font-bold ${s.color}`}>
                      {s.value}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <h3
                  className={`text-sm font-semibold mb-3 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Assignment Breakdown
                </h3>
                <div
                  className="space-y-2 overflow-y-auto"
                  style={{ maxHeight: "360px" }}
                >
                  {previewData.assignments?.map((a) => (
                    <div
                      key={a.userId}
                      className={`flex items-center justify-between p-3 rounded-lg border ${cardCls}`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-medium overflow-hidden ${
                            isDarkMode
                              ? "bg-gray-700 text-gray-300"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {a.user?.profileImageUrl ? (
                            <img
                              src={a.user.profileImageUrl}
                              alt={a.user.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            a.user?.name?.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <div
                            className={`text-sm font-medium ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {a.user?.name}
                          </div>
                          <div className={`text-xs ${mutedText}`}>
                            Current: {a.currentLeadCount} callbacks
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-amber-400">
                          +{a.leadsToAssign}
                        </div>
                        <div className={`text-xs ${mutedText}`}>
                          Total: {a.currentLeadCount + a.leadsToAssign}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {previewData.warnings?.length > 0 && (
                <div className="p-3 bg-orange-500/10 border border-orange-500/40 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle
                      size={15}
                      className="text-orange-400 mt-0.5 flex-shrink-0"
                    />
                    <div>
                      <p className="text-xs font-medium text-orange-400 mb-1">
                        Warnings
                      </p>
                      <ul className={`text-xs space-y-0.5 ${mutedText}`}>
                        {previewData.warnings.map((w, i) => (
                          <li key={i}>· {w}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-between p-5 border-t flex-shrink-0 ${
            isDarkMode
              ? "border-gray-700 bg-gray-800/50"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            onClick={() => (step > 1 ? setStep(step - 1) : onClose())}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg transition-colors disabled:opacity-50 ${
              isDarkMode
                ? "text-gray-400 hover:text-white hover:bg-gray-700"
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-200"
            }`}
          >
            {step === 1 ? "Cancel" : "← Back"}
          </button>

          <div className="flex items-center gap-2">
            {step < 3 && (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed() || loading}
                className="px-5 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors font-medium"
              >
                Next →
              </button>
            )}

            {step === 3 && (
              <button
                onClick={getPreviewData}
                disabled={!canProceed() || loading}
                className="flex items-center gap-2 px-5 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors font-medium"
              >
                <Filter size={15} />
                {loading ? "Generating..." : "Preview Assignment"}
              </button>
            )}

            {step === 4 && (
              <button
                onClick={executeAssignment}
                disabled={loading}
                className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors font-medium"
              >
                <Play size={15} />
                {loading ? "Assigning..." : "Execute Assignment"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoAssignCallbacksModal;
